import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeroSlider from '../components/home/HeroSlider';
import FeaturedCollections from '../components/home/FeaturedCollections';
import FeaturedProducts from '../components/home/FeaturedProducts';
import { supabase } from '../lib/supabase';
import SeoHead from '../components/seo/SeoHead';
import { useStoreSettings } from '../hooks/useStoreSettings';
import styles from './Home.module.css';

interface Banner {
  id: string;
  title: string;
  description: string;
  subtitle?: string;
  image_url: string;
  link_url: string;
  is_active: boolean;
  display_order: number;
  content_position?: 'left' | 'center' | 'right';
  content_vertical_align?: 'top' | 'middle' | 'bottom';
  content_horizontal_offset?: number;
  content_vertical_offset?: number;
  video_url?: string;
  is_video?: boolean;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  show_content?: boolean;
}

interface HomepageConfig {
  show_banners: boolean;
  show_categories: boolean;
  show_featured_products: boolean;
  products_show_heading: boolean;
  products_show_description: boolean;
  banners_count: number;
  categories_count: number;
  featured_products_count: number;
  display_order_banners: number;
  display_order_categories: number;
  display_order_products: number;
  categories_show_image?: boolean;
  categories_per_row?: number;
  categories_show_description?: boolean;
}

const Home: React.FC = () => {
  const [activeBanner, setActiveBanner] = useState<Banner | null>(null);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [homepageConfig, setHomepageConfig] = useState<HomepageConfig>({
    show_banners: true,
    show_categories: true,
    show_featured_products: true,
    products_show_heading: true,
    products_show_description: true,
    banners_count: 3,
    categories_count: 4,
    featured_products_count: 8,
    display_order_banners: 1,
    display_order_categories: 2,
    display_order_products: 3,
    categories_show_image: true,
    categories_per_row: 0,
    categories_show_description: true
  });
  const [loadingBanners, setLoadingBanners] = useState(true);
  
  useEffect(() => {
    const fetchHomepageData = async () => {
      setLoadingBanners(true);
      try {
        // Fetch homepage config (RLS scopes to current tenant; only one row).
        const { data: configData, error: configError } = await supabase
          .from('homepage_config')
          .select('*')
          .limit(1)
          .maybeSingle();
          
        if (configError) {
          console.error('Error fetching homepage config:', configError);
        }
        
        console.log('Fetched homepage config:', configData);
        
        // Default konfiguracija ako ne postoji u bazi
        const defaultConfig = {
          show_banners: true,
          show_categories: true,
          show_featured_products: true,
          products_show_heading: true,
          products_show_description: true,
          banners_count: 3,
          categories_count: 4,
          featured_products_count: 8,
          display_order_banners: 1,
          display_order_categories: 2,
          display_order_products: 3,
          categories_show_image: true,
          categories_per_row: 0,
          categories_show_description: true
        };

        // Spajamo default vrednosti sa onima iz baze
        const mergedConfig = {
          ...defaultConfig,
          ...(configData || {})
        };
        
        console.log('Merged config:', mergedConfig);
        setHomepageConfig(mergedConfig);
        
        // Only fetch banners if they should be shown
        if (mergedConfig.show_banners !== false) {
          // Fetch active banners
          const { data: bannerData, error: bannerError } = await supabase
            .from('store_banners')
            .select('*')
            .eq('is_active', true)
            .order('display_order', { ascending: true })
            .limit(mergedConfig.banners_count || 3);
            
          if (bannerError) {
            console.error('Error fetching banners:', bannerError);
          }
          
          console.log('Fetched banners:', bannerData);
          
          if (bannerData && bannerData.length > 0) {
            // Remap data to ensure we have consistent field names
            const formattedBanners = bannerData.map(banner => ({
              ...banner,
              // Ensure subtitle exists for HeroSlider (using description field)
              subtitle: banner.description
            }));
            
            setBanners(formattedBanners);
            setActiveBanner(formattedBanners[0]);
            console.log('Set active banner:', formattedBanners[0]);
          } else {
            console.log('No active banners found');
          }
        } else {
          console.log('Banners disabled in config');
        }
      } catch (error) {
        console.error('Error fetching homepage data:', error);
      } finally {
        setLoadingBanners(false);
      }
    };
    
    fetchHomepageData();
  }, []);
  
  // Funkcija za promenu aktivnog bannera (za slideshow)
  const handleBannerChange = (index: number) => {
    if (banners[index]) {
      setActiveBanner(banners[index]);
    }
  };
  
  // Timer za automatsku promenu bannera
  useEffect(() => {
    if (banners.length <= 1) return;
    
    const interval = setInterval(() => {
      const currentIndex = banners.findIndex(b => b.id === activeBanner?.id);
      const nextIndex = (currentIndex + 1) % banners.length;
      setActiveBanner(banners[nextIndex]);
    }, 5000); // Menjaj banner svakih 5 sekundi
    
    return () => clearInterval(interval);
  }, [activeBanner, banners]);
  
  // Funkcija za sortiranje elemenata po redosledu
  const getSortedSections = () => {
    // Kreiramo nizove elemenata sa njihovim redosledom prikaza
    const sections = [
      { type: 'banners', order: homepageConfig.display_order_banners, visible: homepageConfig.show_banners && !!activeBanner },
      { type: 'categories', order: homepageConfig.display_order_categories, visible: homepageConfig.show_categories },
      { type: 'products', order: homepageConfig.display_order_products, visible: homepageConfig.show_featured_products }
    ];
    
    // Sortiramo prema polju 'order'
    return sections.sort((a, b) => a.order - b.order);
  };
  
  // Renderovanje sekcija prema sortiranom redosledu
  const renderSection = (type: string) => {
    switch(type) {
      case 'banners':
        return homepageConfig.show_banners && activeBanner ? (
          <section key="banners" className={styles.bannerSection}>
            <HeroSlider 
              title={activeBanner.title || 'Dobrodošli u našu prodavnicu'}
              subtitle={activeBanner.description || activeBanner.subtitle || 'Otkrijte naše najnovije proizvode'}
              imageUrl={activeBanner.image_url}
              contentPosition={activeBanner.content_position || 'left'}
              contentVerticalAlign={activeBanner.content_vertical_align || 'middle'}
              contentHorizontalOffset={activeBanner.content_horizontal_offset || 0}
              contentVerticalOffset={activeBanner.content_vertical_offset || 0}
              videoUrl={activeBanner.video_url}
              isVideo={activeBanner.is_video}
              autoplay={activeBanner.autoplay}
              muted={activeBanner.muted}
              loop={activeBanner.loop}
              showContent={activeBanner.show_content !== false}
              buttonText={(activeBanner as any).button_text}
              buttonLink={activeBanner.link_url || (activeBanner as any).button_link}
              buttonBgColor={(activeBanner as any).button_bg_color}
              buttonTextColor={(activeBanner as any).button_text_color}
              buttonRadius={(activeBanner as any).button_radius}
              fontFamily={(activeBanner as any).font_family}
              overlayOpacity={(activeBanner as any).overlay_opacity}
              backgroundColor={(activeBanner as any).background_color}
            />
            
            {/* Indikatori za bannere (tačkice) */}
            {banners.length > 1 && (
              <div className={styles.bannerIndicators}>
                {banners.map((banner, index) => (
                  <button
                    key={banner.id}
                    className={`${styles.indicator} ${activeBanner.id === banner.id ? styles.active : ''}`}
                    onClick={() => handleBannerChange(index)}
                    aria-label={`Slide ${index + 1}`}
                  ></button>
                ))}
              </div>
            )}
          </section>
        ) : null;
      
      case 'categories':
        return homepageConfig.show_categories ? (
          <section key="categories" className={styles.categoriesSection}>
            <div className={styles.sectionHeader}>
              <h2>Pretraži po kategorijama</h2>
              <Link to="/categories" className={styles.viewAllLink}>
                Pregledaj sve
              </Link>
            </div>
            <FeaturedCollections
              limit={homepageConfig.categories_count}
              showImage={homepageConfig.categories_show_image !== false}
              perRow={homepageConfig.categories_per_row || 0}
              showDescription={homepageConfig.categories_show_description !== false}
            />
          </section>
        ) : null;
      
      case 'products':
        return homepageConfig.show_featured_products ? (
          <section key="products" className={styles.productsSection}>
            {homepageConfig.products_show_heading !== false && (
              <div className={styles.sectionHeader}>
                <h2>Popularni proizvodi</h2>
                <Link to="/products" className={styles.viewAllLink}>
                  Pregledaj sve
                </Link>
              </div>
            )}
            <FeaturedProducts
              limit={homepageConfig.featured_products_count}
              showDescriptions={homepageConfig.products_show_description !== false}
            />
          </section>
        ) : null;
      
      default:
        return null;
    }
  };
  
  return (
    <div className={styles.homePage}>
      <HomeSeo />
      {/* Renderjemo sekcije po sortiranom redosledu */}
      {getSortedSections()
        .filter(section => section.visible)
        .map(section => renderSection(section.type))
      }
    </div>
  );
};

const HomeSeo: React.FC = () => {
  const { settings } = useStoreSettings();
  const seoDescription = (settings as any)?.seo_default_description as string | undefined;
  const logoUrl = (settings?.header_content as any)?.logo?.url as string | undefined;
  const storeName = settings?.store_name?.trim() || 'Shop';

  const organizationLd = {
    '@context': 'https://schema.org',
    '@type': 'OnlineStore',
    name: storeName,
    url: typeof window !== 'undefined' ? window.location.origin : undefined,
    logo: logoUrl,
    image: logoUrl,
  };

  return (
    <SeoHead
      description={seoDescription}
      image={logoUrl}
      jsonLd={organizationLd}
    />
  );
};

export default Home; 